<template>
    <div class="chaowei-prescription-page">
        <!-- 装饰性模糊背景圆（参照 PrescriptionPage） -->
        <div class="bg-deco bg-deco-left"></div>
        <div class="bg-deco bg-deco-right"></div>

        <div class="page-container">
            <!-- 顶部返回栏 -->
            <PageHeader :title="config.pageTitle" @back="goBack" />

            <!-- 加载中 -->
            <div v-if="loading" class="page-loading">
                <i class="el-icon-loading"></i>
                <div class="loading-text">正在生成调理方案，请稍候...</div>
            </div>

            <!-- 方案内容 -->
            <div v-else-if="groupedResult" class="page-body">
                <!-- 基本信息卡（可折叠，默认折叠） -->
                <div class="symptom-section basic-info-section">
                    <el-collapse v-model="basicInfoActiveNames" class="symptom-collapse basic-info-collapse">
                        <el-collapse-item name="basicInfo">
                            <template slot="title">
                                <div class="collapse-title-bar">
                                    <span class="collapse-accent"></span>
                                    <span class="collapse-title-text">基本信息</span>
                                    <span class="collapse-hint" v-if="!basicInfoExpanded">点击展开</span>
                                </div>
                            </template>
                            <div class="basic-info-content">
                                <div class="info-item">
                                    <span class="info-label">姓名：</span>
                                    <span class="info-value">{{ patientName }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">性别：</span>
                                    <span class="info-value">{{ patientGender }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">年龄：</span>
                                    <span class="info-value">{{ patientAge }}</span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">方案类型：</span>
                                    <span class="info-value">药食同源（{{ timeTypeLabel }}）</span>
                                </div>
                                <div class="info-item" v-if="groupedResult.startDate">
                                    <span class="info-label">开始日期：</span>
                                    <span class="info-value">{{ formatDateStr(groupedResult.startDate) }}</span>
                                </div>
                                <div class="info-item" v-if="groupedResult.takeDays">
                                    <span class="info-label">服用天数：</span>
                                    <span class="info-value">{{ groupedResult.takeDays }} 天</span>
                                </div>
                                <div class="info-item" v-if="groupedResult.takeHours">
                                    <span class="info-label">服用时辰：</span>
                                    <span class="info-value">{{ groupedResult.takeHours }}</span>
                                </div>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>

                <!-- 症状信息卡（可折叠，默认折叠，仅在有数据时显示） -->
                <div class="symptom-section" v-if="hasSymptomData">
                    <el-collapse v-model="symptomsActiveNames" class="symptom-collapse">
                        <el-collapse-item name="symptoms">
                            <template slot="title">
                                <div class="collapse-title-bar">
                                    <span class="collapse-accent"></span>
                                    <span class="collapse-title-text">{{ effectiveSymptomsTitle }}</span>
                                    <span class="collapse-hint" v-if="!symptomExpanded">点击展开</span>
                                </div>
                            </template>
                            <div class="symptom-list">
                                <!-- 基本情况 / 脉象 -->
                                <div class="symptom-group" v-if="basicTableArray.length > 0">
                                    <div class="symptom-group-title">{{ effectiveBasicGroupTitle }}</div>
                                    <div class="symptom-info-grid">
                                        <div class="info-item" v-for="item in basicTableArray" :key="item.key">
                                            <span class="info-label">{{ item.key === '主诉' ? '问询结果' : item.key }}：</span>
                                            <span class="info-value">{{ formatValue(item.value) }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- 三焦辨证 -->
                                <template
                                    v-if="shangjiaoTableArray.length > 0 || zhongjiaoTableArray.length > 0 || xiajiaoTableArray.length > 0">
                                    <div class="divider" v-if="basicTableArray.length > 0"></div>
                                    <div class="symptom-group">
                                        <div class="symptom-group-title">三焦情况</div>
                                        <div class="sanjiao-list">
                                            <div class="sanjiao-group" v-if="shangjiaoTableArray.length > 0">
                                                <div class="sanjiao-group-title">上焦</div>
                                                <div class="symptom-info-grid">
                                                    <div class="info-item" v-for="item in shangjiaoTableArray"
                                                        :key="item.key">
                                                        <span class="info-label">{{ item.key }}：</span>
                                                        <span class="info-value">{{ formatValue(item.value) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="sanjiao-group" v-if="zhongjiaoTableArray.length > 0">
                                                <div class="sanjiao-group-title">中焦</div>
                                                <div class="symptom-info-grid">
                                                    <div class="info-item" v-for="item in zhongjiaoTableArray"
                                                        :key="item.key">
                                                        <span class="info-label">{{ item.key }}：</span>
                                                        <span class="info-value">{{ formatValue(item.value) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="sanjiao-group" v-if="xiajiaoTableArray.length > 0">
                                                <div class="sanjiao-group-title">下焦</div>
                                                <div class="symptom-info-grid">
                                                    <div class="info-item" v-for="item in xiajiaoTableArray"
                                                        :key="item.key">
                                                        <span class="info-label">{{ item.key }}：</span>
                                                        <span class="info-value">{{ formatValue(item.value) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>

                <!-- 顶层分组 Tab + 方案内容统一放进大白色圆角卡片（参照 prescription 的 .tab-card） -->
                <section class="chaowei-tab-card" v-if="groupTabs.length >= 1">
                    <!-- <div class="tab-bar">
            <div class="tab-buttons">
              <button
                v-for="tab in groupTabs"
                :key="tab.key"
                :class="['tab-pill', { 'tab-pill-active': activeTab === tab.key }]"
                @click="activeTab = tab.key"
              >{{ tab.label }}</button>
            </div>
          </div> -->

                    <!-- 方案子 Tab（参照 PrescriptionPage 的 sub-tabs，方案名作为 pane label） -->
                    <el-tabs v-if="activePrescriptions.length >= 1" v-model="activeRxTabName" class="sub-tabs">
                        <el-tab-pane v-for="(rx, index) in activePrescriptions" :key="index"
                            :label="rx.name || ('调理方案' + (index + 1))" :name="String(index)" />
                    </el-tabs>

                    <!-- 当前选中方案（与 prescription 一比一：表格直接暴露，方案名由 el-tabs active label 显示） -->
                    <div class="prescription-list" v-if="activePrescription">
                        <PrescriptionTable v-if="activePrescriptionPane" :pane="activePrescriptionPane"
                            :consultInfo="consultInfo" :config="tableConfig" />

                        <!-- 方案使用说明（保留 chaowei 流程特有信息） -->
                        <div class="usage-block" v-if="activePrescription.usageInstructions">
                            <div class="usage-title">用法用量</div>
                            <div class="usage-text">{{ activePrescription.usageInstructions }}</div>
                        </div>
                        <div class="usage-block warn" v-if="activePrescription.contraIndications">
                            <div class="usage-title">注意事项</div>
                            <div class="usage-text">{{ activePrescription.contraIndications }}</div>
                        </div>
                    </div>
                </section>



                <!-- 底部操作栏（参照 prescription 的胶囊按钮风格） -->
                <div class="bottom-actions">
                    <button class="btn-bottom-draft" @click="sendPrescriptionToAi" :disabled="!activePrescription">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        一键输入到AI对话框
                    </button>
                    <button v-if="isDelivered || !isExpired" class="btn-bottom-submit btn-row-fullwidth"
                        @click="goFulfillment">
                        <svg v-if="isDelivered" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M9 12l2 2 4-4" />
                        </svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="6" width="14" height="12" rx="2" />
                            <path d="M16 10h4l2 3v5h-6z" />
                            <circle cx="6.5" cy="19.5" r="1.5" />
                            <circle cx="18.5" cy="19.5" r="1.5" />
                        </svg>
                        {{ isDelivered ? "配送详情" : "生成调理方案" }}
                    </button>
                    <button v-else class="btn-bottom-delivery btn-bottom-delivery-disabled" disabled>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v6M12 17h.01" />
                        </svg>
                        调理方案已过期
                    </button>
                    <!-- <button class="btn-bottom-submit btn-row-fullwidth" @click="regeneratePrescription">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            生成方案
          </button> -->
                </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="page-empty">
                <i class="el-icon-document-remove"></i>
                <div class="empty-text">未找到调理方案信息</div>
                <el-button type="primary" @click="goBackToContact">去调理</el-button>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from '@/components/widgets/PageHeader.vue'
import PrescriptionTable from './PrescriptionTable.vue'
import { EventBus } from '@/js/eventbus.js'
import utils from '@/js/utils.js'

export default {
    name: "ChaoweiPrescriptionPage",
    components: { PageHeader, PrescriptionTable },
    props: {
        config: {
            type: Object,
            default: () => ({
                pageTitle: "调理方案信息",
                fetchApi: "/api/health/v2/beveragePrescription/grouped",
                contactRoute: "/chaoweiContact",
                homeRoute: "/",
                fulfillmentRoute: "/fulfillmentOrder",
                fulfillmentConfirmRoute: "/fulfillmentConfirm",
                prescriptionImageRoute: "/chaoweiPrescriptionImage",
            }),
        },
    },
    data() {
        return {
            loading: false,
            visitRecordId: "",
            groupedResult: null,
            activeTab: "",
            activeRxIndex: 0, // 当前方案页签索引（超微饮片1/2/...）
            activeRxTabName: "0", // el-tabs 绑定的 pane name（字符串）
            basicInfoActiveNames: [], // 默认折叠
            symptomsActiveNames: [], // 默认折叠
            consultInfo: {},
            submitData: {},
            basicTableArray: [],
            shangjiaoTableArray: [],
            zhongjiaoTableArray: [],
            xiajiaoTableArray: [],
            tableConfig: {
                showSupplyDays: true,
                // 超微饮品场景：表头/汇总/有效期改为超微方文案
                chaoweiMode: true,
            },
        };
    },
    watch: {
        // 切换月方/日方/时方分组时，回到该分组第一个方案
        activeTab() {
            this.activeRxIndex = 0;
        },
        // el-tabs 的 v-model 变化时，同步到 activeRxIndex
        activeRxTabName(val) {
            var idx = parseInt(val, 10);
            if (!isNaN(idx) && idx !== this.activeRxIndex) {
                this.activeRxIndex = idx;
            }
        },
    },
    computed: {
        patient() {
            return (this.groupedResult && this.groupedResult.patient) || {};
        },
        patientName() {
            return this.patient.name || this.patient.patientName || "-";
        },
        patientGender() {
            if (this.patient.sexDesc) return this.patient.sexDesc;
            const gender = this.patient.gender || this.patient.sex;
            if (gender === "0" || gender === 0) return "女";
            if (gender === "1" || gender === 1) return "男";
            return gender || "-";
        },
        patientAge() {
            const age = this.patient.age;
            if ((age === 0 || age) && age !== "") return age + " 岁";
            // age 为空时根据生日推算
            const birthday = this.patient.birthday;
            if (!birthday) return "-";
            const birth = new Date(birthday);
            if (isNaN(birth.getTime())) return "-";
            const now = new Date();
            let computed = now.getFullYear() - birth.getFullYear();
            const monthDiff = now.getMonth() - birth.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
                computed--;
            }
            return computed > 0 ? computed + " 岁" : "-";
        },
        timeTypeLabel() {
            const timeType = (this.groupedResult && this.groupedResult.ganZhiPrescriptionTimeType) || "";
            if (timeType === "monthly") return "月方案";
            if (timeType === "daily") return "日方案";
            if (timeType === "hourly") return "时方案";
            return "超微饮品";
        },
        groupTabs() {
            const groups = (this.groupedResult && this.groupedResult.groups) || [];
            return groups.map((g) => ({ key: g.type || g.label, label: g.label || g.name || g.type }));
        },
        activePrescriptions() {
            const groups = (this.groupedResult && this.groupedResult.groups) || [];
            const target = groups.find((g) => (g.type || g.name) === this.activeTab);
            return (target && target.list) || [];
        },
        // 当前选中的方案
        activePrescription() {
            return this.activePrescriptions[this.activeRxIndex] || null;
        },
        // 当前就诊记录是否已配送（Y-已配送）
        isDelivered() {
            return this.groupedResult && this.groupedResult.deliveryFlag === "Y";
        },
        // 当前方案是否已过期（参照普通方 dealDeliveryInfo 的 DateUtils.isExpire(endDate) 逻辑）
        // 过期规则：endDate <= 今天 视为过期，到期当天不可配送
        isExpired() {
            var rx = this.activePrescription;
            if (!rx || !rx.endDate) return false;
            var endStr = String(rx.endDate);
            // 提取 endDate 的「年月日」数字（兼容 yyyyMMdd / yyyy-MM-dd / yyyy/MM/dd 格式）
            var digits = endStr.replace(/\D/g, ""); // 去掉所有非数字字符
            if (digits.length < 8) return false;
            var endY = parseInt(digits.substring(0, 4), 10);
            var endM = parseInt(digits.substring(4, 6), 10);
            var endD = parseInt(digits.substring(6, 8), 10);
            if (!endY || !endM || !endD) return false;
            // 取「今天」的日期（忽略时分秒）
            var now = new Date();
            var todayY = now.getFullYear();
            var todayM = now.getMonth() + 1;
            var todayD = now.getDate();
            // 过期：endDate 的日期 <= 今天的日期（与后端 DateUtils.isExpire 一致，当天即过期）
            if (endY < todayY) return true;
            if (endY > todayY) return false;
            if (endM < todayM) return true;
            if (endM > todayM) return false;
            return endD <= todayD;
        },
        // 基本信息卡是否处于展开状态
        basicInfoExpanded() {
            return this.basicInfoActiveNames && this.basicInfoActiveNames.indexOf("basicInfo") !== -1;
        },
        // 症状信息卡是否处于展开状态
        symptomExpanded() {
            return this.symptomsActiveNames && this.symptomsActiveNames.indexOf("symptoms") !== -1;
        },
        // 是否有症状数据
        hasSymptomData() {
            return this.basicTableArray.length > 0
                || this.shangjiaoTableArray.length > 0
                || this.zhongjiaoTableArray.length > 0
                || this.xiajiaoTableArray.length > 0;
        },
        // chaowei 流程下症状卡 title 显示「健康问询」，否则「症状信息」
        effectiveSymptomsTitle() {
            if (this.$route.path.indexOf("chaowei") !== -1) return "基本情况";
            return "症状信息";
        },
        // chaowei 流程下基本情况/脉象标题显示「基本情况」，否则「基本情况 / 脉象」
        effectiveBasicGroupTitle() {
            if (this.$route.path.indexOf("chaowei") !== -1) return "基本情况";
            return "基本情况 / 脉象";
        },
        // 构造 PrescriptionTable 所需的 pane 数据
        activePrescriptionPane() {
            var rx = this.activePrescription;
            if (!rx) return null;
            var list = (rx.prescription || []).map((drug) => Object.assign({}, drug));
            // 计算 totalWeight（药品 value(g) 之和，后端可能未返回）
            var computedWeight = 0;
            var hasWeight = false;
            for (var i = 0; i < list.length; i++) {
                var w = Number(list[i].value);
                if (!isNaN(w)) {
                    computedWeight += w;
                    hasWeight = true;
                }
            }
            // 两两配对成一行（双列布局）
            var options = [];
            for (var k = 0; k < list.length; k += 2) {
                var left = list[k] || {};
                var right = list[k + 1];
                var pair = Object.assign({}, left);
                if (right && right.key != null && right.key !== "") {
                    pair.key1 = right.key;
                    pair.value1 = right.value;
                    pair.index1 = right.index;
                }
                options.push(pair);
            }
            // 计算 supplyDays（startDate ~ endDate 之间的天数）
            var supplyDays = rx.supplyDays;
            if (supplyDays == null) {
                var s = this.formatDateStr(rx.startDate);
                var e = this.formatDateStr(rx.endDate);
                var sd = new Date(s);
                var ed = new Date(e);
                if (!isNaN(sd.getTime()) && !isNaN(ed.getTime())) {
                    supplyDays = Math.round((ed - sd) / (1000 * 60 * 60 * 24)) + 1;
                }
            }
            return {
                title: rx.name || ("调理方案" + (this.activeRxIndex + 1)),
                count: rx.count || list.length,
                charge: rx.charge || 0,
                totalWeight: rx.totalWeight != null ? rx.totalWeight : (hasWeight ? computedWeight : 0),
                startDate: this.formatDateStr(rx.startDate),
                endDate: this.formatDateStr(rx.endDate),
                supplyDays: supplyDays,
                isHandmade: false,
                options: options,
            };
        },
    },
    mounted() {
        this.visitRecordId = this.$route.query.visitRecordId || sessionStorage.getItem("visitRecordId") || "";
        if (this.visitRecordId) {
            sessionStorage.setItem("visitRecordId", this.visitRecordId);
        }
        if (!this.visitRecordId) {
            this.$message.warning("请先完成药食同源调理流程");
            return;
        }
        this.loadSubmitData();
        this.loadGroupedResult();
    },
    methods: {
        // 从 sessionStorage 读取 submitData / consultInfo（来自 prescriptionInfo 提交的健康问询）
        loadSubmitData() {
            try {
                var consult = JSON.parse(sessionStorage.getItem("consultInfo") || "null");
                if (consult) this.consultInfo = consult;
                var sd = JSON.parse(sessionStorage.getItem("submitData") || "null");
                if (sd) {
                    this.submitData = sd;
                    this.parseSymptomData(sd);
                }
            } catch (e) {
                console.warn("[ChaoweiPrescriptionPage] loadSubmitData parse error:", e);
            }
        },
        // 解析症状信息：参照 PrescriptionResultPage 的 initPageData 逻辑
        parseSymptomData(submitData) {
            this.basicTableArray = [];
            this.shangjiaoTableArray = [];
            this.zhongjiaoTableArray = [];
            this.xiajiaoTableArray = [];

            var basicArray = submitData && submitData.basic_array;
            if (!this.utils.isEmptyObject(basicArray)) {
                for (var i = 0; i < basicArray.length; i++) {
                    if (i == 0) {
                        this.basicTableArray.push(basicArray[i]);
                    } else {
                        if (basicArray[i].value != "正常") {
                            this.basicTableArray.push(basicArray[i]);
                        }
                    }
                }
            }
            var basicEnterArray = submitData && submitData.basic_enter_array;
            if (!this.utils.isEmptyObject(basicEnterArray)) {
                for (var j = 0; j < basicEnterArray.length; j++) {
                    if (basicEnterArray[j].value && basicEnterArray[j].value.length > 0) {
                        this.basicTableArray.push(basicEnterArray[j]);
                    }
                }
            }
            var maixiangArray = submitData && submitData.maixiang_array;
            if (!this.utils.isEmptyObject(maixiangArray)) {
                for (var m = 0; m < maixiangArray.length; m++) {
                    if (maixiangArray[m].value != "正常") {
                        this.basicTableArray.push(maixiangArray[m]);
                    }
                }
            }
            var shangjiaoArray = submitData && submitData.shangjiao_array;
            if (!this.utils.isEmptyObject(shangjiaoArray)) {
                for (var s = 0; s < shangjiaoArray.length; s++) {
                    if (shangjiaoArray[s].value && shangjiaoArray[s].value.length > 0) {
                        this.shangjiaoTableArray.push(shangjiaoArray[s]);
                    }
                }
            }
            var zhongjiaoArray = submitData && submitData.zhongjiao_array;
            if (!this.utils.isEmptyObject(zhongjiaoArray)) {
                for (var z = 0; z < zhongjiaoArray.length; z++) {
                    if (zhongjiaoArray[z].value && zhongjiaoArray[z].value.length > 0) {
                        this.zhongjiaoTableArray.push(zhongjiaoArray[z]);
                    }
                }
            }
            var xiajiaoArray = submitData && submitData.xiajiao_array;
            if (!this.utils.isEmptyObject(xiajiaoArray)) {
                for (var x = 0; x < xiajiaoArray.length; x++) {
                    if (xiajiaoArray[x].value && xiajiaoArray[x].value.length > 0) {
                        this.xiajiaoTableArray.push(xiajiaoArray[x]);
                    }
                }
            }
        },
        loadGroupedResult() {
            this.loading = true;
            this.wjtHttp.post(this, this.config.fetchApi, {
                visitRecordId: this.visitRecordId,
            }, false, (res) => {
                this.loading = false;
                var data = res.data;
                if (data.code == 200) {
                    this.groupedResult = data.data || null;
                    if (this.groupTabs.length > 0) {
                        this.activeTab = this.groupTabs[0].key;
                    }
                    if (!this.groupedResult || this.groupTabs.length === 0) {
                        this.$message.warning("调理方案结果为空");
                    }
                    // 优先使用接口返回的 inquiryInfo 字段（与后端 /api/health/v2/price 同步，含健康问询数据）
                    if (this.groupedResult && this.groupedResult.inquiryInfo) {
                        this.parseSymptomData(this.groupedResult.inquiryInfo);
                    }
                } else {
                    this.$message.error(data.msg || "获取调理方案失败");
                }
            }, (err) => {
                console.error(err);
                this.loading = false;
                this.$message.error("获取调理方案失败，请稍后重试");
            });
        },
        // 格式化 20260815 → 2026-08-15
        formatDateStr(value) {
            if (!value) return "-";
            const str = String(value);
            if (str.length === 8) {
                return str.substring(0, 4) + "-" + str.substring(4, 6) + "-" + str.substring(6, 8);
            }
            return str;
        },
        // 格式化症状值（数组 → 中文逗号分隔）
        formatValue(value) {
            if (Array.isArray(value)) {
                return value.join("，").replace(/,/g, "，");
            }
            if (typeof value === "string") {
                return value.replace(/,/g, "，");
            }
            return value;
        },
        goBack() {
            this.$router.go(-1);
        },
        goHome() {
            this.$router.push(this.config.homeRoute);
        },
        goBackToContact() {
            this.$router.push(this.config.contactRoute);
        },
        newPrescription() {
            sessionStorage.removeItem("visitRecordId");
            sessionStorage.removeItem("consultInfo");
            sessionStorage.removeItem("submitData");
            this.$router.push(this.config.contactRoute);
        },
        // 重新生成超微饮品方案（参照 prescription 的 submit 思路，重新调后端接口生成）
        regeneratePrescription() {
            if (!this.visitRecordId) {
                this.$message.warning("缺少就诊记录编号，无法重新生成方案");
                return;
            }
            // 重新调后端 /api/health/v2/beveragePrescription/grouped，重新获取新版超微方
            this.$message.success("已请求重新生成方案，请稍候...");
            this.loadGroupedResult();
        },
        // 去配送 / 配送详情
        goFulfillment() {
            if (this.isDelivered) {
                // 已配送：跳转配送订单详情
                this.$router.push({
                    path: this.config.fulfillmentRoute,
                    query: {
                        orderId: this.groupedResult.deliveryOrderId,
                    },
                });
                return;
            }
            // 过期方案不可配送（后端也会二次校验，这里做前置拦截提升体验）
            if (this.isExpired) {
                this.$message.warning("调理方案已过期，无法配送");
                return;
            }
            // 代理商演示账号禁止配送下单
            var userInfo = this.$store.getters.getUserInfo;
            var roles = (userInfo && userInfo.roles) || [];
            var isAgentDemo = false;
            for (var i = 0; i < roles.length; i++) {
                if (roles[i] == "level1" || roles[i] == "level2") {
                    isAgentDemo = true;
                    break;
                }
            }
            // if (isAgentDemo) {
            //     this.$message.warning("当前为演示账号，配送下单功能暂未开放");
            //     return;
            // }
            var prescription = this.activePrescription;
            if (!prescription) {
                this.$message.warning("请先选择需要配送的方案");
                return;
            }
            // 构造配送所需的方案对象：period_mode 显式映射为超微方类型（后端已统一，这里做兼容兜底）
            var periodMode = prescription.type || this.groupedResult.ganZhiPrescriptionTimeType || "";
            var typeMap = {
                month: "monthly_superfine_prescription",
                monthly: "monthly_superfine_prescription",
                day: "daily_superfine_prescription",
                daily: "daily_superfine_prescription",
                hour: "hoursly_superfine_prescription",
                hourly: "hoursly_superfine_prescription",
                hoursly: "hoursly_superfine_prescription",
            };
            var mappedType = typeMap[periodMode];
            if (!mappedType) {
                // 已是完整超微方类型（如 monthly_superfine_prescription）直接用，否则才走拼接
                if (periodMode && periodMode.indexOf("_superfine_prescription") !== -1) {
                    mappedType = periodMode;
                } else {
                    mappedType = periodMode + "_superfine_prescription";
                }
            }
            var deliveryPrescription = {
                type: mappedType,
                visitRecordId: this.visitRecordId,
                startDate: prescription.startDate,
                endDate: prescription.endDate,
                name: prescription.name,
                status: "0",
                deliveryOrderId: "",
                imageUrl: "",
                prescriptionNo: "",
            };
            sessionStorage.setItem("prescription", JSON.stringify(deliveryPrescription));
            // 超微饮片场景：保存方案明细(pane)与调理人信息(consultInfo)，供配送确认页回填
            // superfinePane 保存全部分组下的所有方案（数组），配送确认页按方案笺页选中的方案取对应明细
            var allPrescriptions = [];
            var groups = (this.groupedResult && this.groupedResult.groups) || [];
            groups.forEach(function (group) {
                var list = (group && group.list) || [];
                allPrescriptions = allPrescriptions.concat(list);
            });
            var panes = allPrescriptions.map(function (rx) {
                return {
                    title: rx.name || "",
                    count: rx.count || 0,
                    totalWeight: rx.totalWeight || 0,
                    charge: rx.charge || 0,
                    startDate: rx.startDate || "",
                    endDate: rx.endDate || "",
                    supplyDays: rx.supplyDays || "",
                    prescription: rx.prescription || [],
                };
            });
            sessionStorage.setItem("superfinePane", JSON.stringify(panes));
            var patient = this.groupedResult.patient || {};
            sessionStorage.setItem("superfinePatient", JSON.stringify(patient));
            this.$store.commit("updateFulfillmentConfirmData", {});
            // 先跳转超微专属方案笺页，由方案笺页发起配送（参照 prescription 页面流程）
            this.$router.push({
                path: this.config.prescriptionImageRoute,
                query: {
                    visitRecordId: this.visitRecordId,
                    prescriptionType: mappedType,
                },
            });
        },
        // 将当前超微方案内容组装为文本（参照 PrescriptionPage 的 parsePrescriptionToText）
        parsePrescriptionToText() {
            var lines = [];
            var patient = this.groupedResult && this.groupedResult.patient;
            var patientName = (this.consultInfo && this.consultInfo.name) || (patient && (patient.name || patient.patientName));
            if (patientName) lines.push("调理人姓名：" + utils.blurName(String(patientName)));
            var rx = this.activePrescription;
            if (!rx) {
                lines.push("方案内容：暂无方案数据");
                return lines.join("\n");
            }
            var list = (rx.prescription || []).map(function (d) { return Object.assign({}, d); });
            var herbs = [];
            for (var k = 0; k < list.length; k += 2) {
                var left = list[k] || {};
                var right = list[k + 1];
                if (left.key && left.value != null) herbs.push(left.key + (left.value + "包"));
                if (right && right.key1 != null && right.key1 !== "" && right.value1 != null) herbs.push(right.key1 + (right.value1 + "包"));
            }
            lines.push("方案内容：");
            lines.push("品名组成：" + (herbs.length > 0 ? herbs.join("，") : "暂无品名明细"));
            var count = rx.count || list.length || 0;
            var totalWeight = rx.totalWeight != null ? rx.totalWeight : 0;
            lines.push("总计：" + count + "种，" + totalWeight + "包");
            lines.push("使用期限：" + (this.formatDateStr(rx.startDate)) + " 至 " + (this.formatDateStr(rx.endDate)));
            lines.push("有效期：" + (rx.supplyDays || 0) + "天");
            return lines.join("\n");
        },
        // 一键输入到AI对话框（参照 PrescriptionPage 的 sendPrescriptionToAi）
        sendPrescriptionToAi() {
            var text = this.parsePrescriptionToText();
            if (text.indexOf("暂无") !== -1 || text.indexOf("异常") !== -1 || text.indexOf("无效") !== -1) {
                this.$message.warning(text);
                return;
            }
            var rx = this.activePrescription;
            var patient = this.groupedResult && this.groupedResult.patient;
            var patientName = (this.consultInfo && this.consultInfo.name) || (patient && (patient.name || patient.patientName));
            EventBus.$emit("ai-input-text", {
                text: text,
                source: "prescription",
                prescriptionType: (rx && rx.name) || this.activeTab || "调理方案",
                patientName: patientName ? utils.blurName(String(patientName)) : "",
            });
            this.$message.success("方案已填充到AI对话框，可以开始咨询");
        },
    },
};
</script>

<style scoped>
.chaowei-prescription-page {
    min-height: 100vh;
    background: #f4f9f5;
    padding-bottom: 40px;
    position: relative;
    overflow: hidden;
}

/* 装饰模糊背景圆（参照 PrescriptionPage） */
.bg-deco {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
}

.bg-deco-left {
    left: -112px;
    top: 96px;
    width: 320px;
    height: 320px;
    background: rgba(42, 122, 75, 0.08);
    filter: blur(64px);
}

.bg-deco-right {
    right: 0;
    top: 0;
    width: 384px;
    height: 288px;
    background: rgba(200, 169, 110, 0.1);
    filter: blur(64px);
}

.page-container {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 24px 80px;
    z-index: 1;
}

.page-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 120px 0;
    color: #20863f;
    font-size: 28px;
}

.loading-text {
    margin-top: 12px;
    font-size: 14px;
    color: #555555;
}

.page-body {
    width: 100%;
}

/* ========== 可折叠 card 通用样式 ========== */
.symptom-section {
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid #e6e6e0;
    border-radius: 22px;
    padding: 16px 22px;
    margin-bottom: 18px;
    box-shadow: 0 6px 24px rgba(28, 55, 35, 0.06);
}

.symptom-collapse {
    border: none;
    background: transparent;
}

.symptom-collapse>>>.el-collapse-item__header {
    border: none;
    background: transparent;
    padding: 4px 0;
}

.symptom-collapse>>>.el-collapse-item__wrap {
    border: none;
    background: transparent;
}

.symptom-collapse>>>.el-collapse-item__content {
    padding: 12px 0 4px;
}

/* 折叠标题条 */
.collapse-title-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.collapse-accent {
    display: inline-block;
    width: 4px;
    height: 18px;
    border-radius: 3px;
    background: #20863F;
}

.collapse-title-text {
    font-size: 18px;
    font-weight: 600;
    color: #123b25;
}

.collapse-hint {
    margin-left: auto;
    font-size: 13px;
    color: #888;
}

/* ========== 基本信息 ========== */
.basic-info-content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding-top: 4px;
}

.info-item {
    display: flex;
    align-items: center;
    border: 1px solid #edf0ec;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 16px;
    line-height: 1.5;
}

.info-label {
    color: #1a1a1a;
    flex-shrink: 0;
    min-width: 100px;
    font-size: 16px;
}

.info-value {
    color: #1a1a1a;
    font-weight: 500;
    font-size: 16px;
    word-break: break-all;
}

@media (max-width: 768px) {
    .basic-info-content {
        grid-template-columns: 1fr;
    }
}

/* ========== 症状信息 ========== */
.symptom-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 4px;
}

.symptom-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.symptom-group-title {
    font-size: 16px;
    font-weight: 600;
    color: #20863F;
    margin-bottom: 4px;
}

.symptom-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.divider {
    height: 1px;
    background: #eee;
    margin: 8px 0;
    width: 100%;
}

.sanjiao-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sanjiao-group {
    border: 1px solid #edf0ec;
    border-radius: 12px;
    padding: 12px 14px;
}

.sanjiao-group-title {
    font-size: 15px;
    font-weight: 600;
    color: #20863F;
    margin-bottom: 8px;
}

@media (max-width: 768px) {
    .symptom-info-grid {
        grid-template-columns: 1fr;
    }
}

/* ========== 顶层分组卡片（参照 prescription 的 .tab-card） ========== */
.chaowei-tab-card {
    background: #ffffff;
    border: 1px solid #e0ebe4;
    border-radius: 32px;
    padding: 24px 28px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    margin-bottom: 24px;
}

/* ========== 顶层分组 Tab（参照 PrescriptionPage 的 tab-bar） ========== */
.tab-bar {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: 1px solid #edf2ee;
    border-radius: 24px;
    background: #fcfdfc;
    padding: 12px;
    margin-bottom: 16px;
}

.tab-buttons {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    margin: 0 auto;
}

.tab-pill {
    flex-shrink: 0;
    padding: 10px 22px;
    border-radius: 9999px;
    border: 1px solid #dfeae3;
    background: #ffffff;
    color: #55655b;
    font-size: 16px !important;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.tab-pill:hover {
    background: #f4f9f5;
}

.tab-pill-active {
    background: #20863F;
    color: #ffffff;
    border-color: #20863F;
    box-shadow: 0 1px 3px rgba(42, 122, 75, 0.2);
}

.tab-pill-active:hover {
    background: #21633d;
}

/* ========== 方案子 Tab（参照 PrescriptionPage 的 sub-tabs） ========== */
.sub-tabs {
    width: 100%;
    margin-bottom: 8px;
}

.sub-tabs>>>.el-tabs__header {
    margin-bottom: 8px;
}

.sub-tabs>>>.el-tabs__nav-wrap::after {
    background-color: #edf2ee;
}

.sub-tabs>>>.el-tabs__item {
    font-size: 17px;
    color: #55655b;
    height: 40px;
    line-height: 40px;
}

.sub-tabs>>>.el-tabs__item.is-active {
    color: #20863F;
    font-weight: 600;
}

.sub-tabs>>>.el-tabs__active-bar {
    background-color: #20863F;
}

/* ========== 方案列表 ========== */
.prescription-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* ========== 用法用量 ========== */
.usage-block {
    margin-top: 14px;
    border-top: 1px dashed #e6e6e0;
    padding-top: 12px;
}

.usage-block.warn .usage-title {
    color: #b46700;
}

.usage-title {
    font-size: 14px;
    font-weight: 600;
    color: #20863f;
    margin-bottom: 6px;
}

.usage-text {
    font-size: 14px;
    color: #555555;
    line-height: 1.7;
    white-space: pre-wrap;
}



/* ========== 底部按钮（参照 prescription 的胶囊按钮风格） ========== */
.bottom-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 520px;
    margin: 32px auto 0;
    padding: 0 0 12px;
    justify-content: center;
    flex-wrap: wrap;
}

@media (min-width: 640px) {
    .bottom-actions {
        flex-direction: row;
        max-width: 780px;
    }
}

.bottom-actions button {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    border-radius: 9999px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    color: #fff;
}

/* 返回首页：白底绿框胶囊 */
.btn-bottom-back-home {
    background: #ffffff;
    border: 1px solid #20863f !important;
    color: #20863f;
    box-shadow: 0 6px 16px rgba(32, 134, 63, 0.12);
}

.btn-bottom-back-home:hover {
    background: #f4f9f5;
}

/* 再开一单：绿色主按钮（参照 prescription 的 btn-bottom-submit） */
/* 一键输入到AI对话框：蓝色主按钮（参照 prescription 的 btn-bottom-draft） */
.btn-bottom-draft {
    background: #1e90ff;
    border: 1px solid #1e90ff !important;
    box-shadow: 0 10px 22px rgba(30, 144, 255, 0.22);
}

.btn-bottom-draft:hover {
    background: #1873cc;
    border-color: #1873cc !important;
    transform: translateY(-2px);
}

.btn-bottom-draft:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

/* 去配送 / 配送详情：橙色胶囊（保持原有 type=warning 视觉） */
.btn-bottom-delivery {
    background: #f59e0b;
    border: 1px solid #f59e0b !important;
    color: #fff;
    box-shadow: 0 10px 22px rgba(245, 158, 11, 0.22);
}

.btn-bottom-delivery:hover {
    background: #d97706;
    border-color: #d97706 !important;
    transform: translateY(-2px);
}

.btn-bottom-delivery-disabled,
.btn-bottom-delivery:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    filter: grayscale(0.4);
}

/* 生成方案：绿色主按钮（参照 prescription 的 btn-bottom-submit） */
.btn-bottom-submit {
    background: #20863F;
    border: 1px solid #20863F !important;
    box-shadow: 0 10px 22px rgba(44, 132, 82, 0.22);
}

.btn-bottom-submit:hover {
    background: #257648;
    border-color: #257648 !important;
    transform: translateY(-2px);
}

/* 跨行整宽：单独占满一整行（flex-basis: 100%） */
.btn-row-fullwidth {
    flex: 1 1 100%;
}

.page-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 120px 0;
    color: #999999;
    font-size: 40px;
}

.empty-text {
    margin: 12px 0 20px;
    font-size: 14px;
    color: #555555;
}
</style>